import React, { useMemo } from "react";
import { X, ListFilter } from "lucide-react";
import type { Filter, FilterValue } from "@/components/Table/Filters";
import { Display_Value_Key as dvk } from "@/components/Create/utils";
import { truncateText } from "@/utils/utilFunctions/truncateText";
import { UserModel } from "@/database";
import { trackEvent, getTrackUserContext } from "@/utils/analytics";

export interface AppliedFilterChipsProps {
  appliedFilters: Record<string, FilterValue>;
  availableFilters: Filter[];
  onRemove: (next: Record<string, FilterValue>) => void;
  onClearAll: () => void;
  onOpenSheet: () => void;
  singleCurrentUser: UserModel | null;
}

const IGNORED_KEYS = new Set(["page", "rows", "sort", "sortBy", "must_not"]);

// Keys that are always absorbed into the "Uploaded by" composite chip.
// `team` is intentionally excluded — it can also be set independently via
// the Team filter, in which case it should render as its own chip.
const UPLOADED_BY_KEYS = new Set(["ctby"]);

function stringifyValue(value: FilterValue): string {
  if (Array.isArray(value)) return value.map((v) => String(v)).join(", ");
  if (typeof value === "object" && value !== null) {
    if ("gt" in value || "lt" in value) {
      const obj = value as { gt?: number; lt?: number };
      const fmt = (ms?: number) =>
        ms ? new Date(ms).toLocaleDateString() : "…";
      return `${fmt(obj.gt)} → ${fmt(obj.lt)}`;
    }
    return JSON.stringify(value);
  }
  return String(value);
}

function deriveUploadedByChip(
  appliedFilters: Record<string, FilterValue>,
  user: UserModel | null
): { label: string; onRemove: () => void } | null {
  if (!user) return null;
  const { ctby, team, must_not } = appliedFilters as any;

  if (Array.isArray(ctby) && ctby.length === 1 && ctby[0] === user.id) {
    return {
      label: "Me",
      onRemove: () => ({}) as any,
    };
  }
  if (
    must_not &&
    typeof must_not === "object" &&
    Array.isArray(must_not.team) &&
    must_not.team.some((t: string) => t === user.team)
  ) {
    return { label: "External", onRemove: () => ({}) as any };
  }
  if (
    Array.isArray(team) &&
    team.length === 1 &&
    String(team[0]) === String(user.team)
  ) {
    return { label: "My team", onRemove: () => ({}) as any };
  }
  return null;
}

const AppliedFilterChips: React.FC<AppliedFilterChipsProps> = ({
  appliedFilters,
  availableFilters,
  onRemove,
  onClearAll,
  onOpenSheet,
  singleCurrentUser,
}) => {
  const filterByName = useMemo(() => {
    const map: Record<string, Filter> = {};
    availableFilters.forEach((f) => {
      map[f.name] = f;
    });
    return map;
  }, [availableFilters]);

  const uploadedByChip = deriveUploadedByChip(
    appliedFilters,
    singleCurrentUser
  );

  const chips = useMemo(() => {
    const list: {
      key: string;
      label: string;
      value: string;
      onRemove: () => void;
    }[] = [];

    Object.keys(appliedFilters).forEach((key) => {
      if (key.endsWith(dvk)) return;
      if (IGNORED_KEYS.has(key)) return;

      // ctby is absorbed only when the composite chip was derived from ctby
      // itself (Me / External). When it was derived from the Team filter
      // ("My team"), ctby was set independently and must render as its own chip.
      if (
        uploadedByChip &&
        UPLOADED_BY_KEYS.has(key) &&
        uploadedByChip.label !== "My team"
      )
        return;
      // team is only absorbed when the composite chip is specifically "My team".
      if (uploadedByChip?.label === "My team" && key === "team") return;

      const filter = filterByName[key];
      if (!filter) return;

      const raw = appliedFilters[key];
      if (raw == null) return;

      const display =
        (appliedFilters[key + dvk] as FilterValue) ?? (raw as FilterValue);
      const text = stringifyValue(display);
      if (!text) return;

      list.push({
        key,
        label: filter.displayName,
        value: text,
        onRemove: () => {
          const next: Record<string, FilterValue> = { ...appliedFilters };
          delete next[key];
          delete (next as any)[`${key}${dvk}`];
          delete (next as any).page;
          trackEvent("browse", {
            category: "filters",
            entity: "AppliedChip",
            action: `remove:${filter.displayName}`,
            location: "content",
            ...getTrackUserContext(),
          });
          onRemove(next);
        },
      });
    });

    if (uploadedByChip) {
      list.unshift({
        key: "__uploadedBy",
        label:
          uploadedByChip.label === "My team" ? "Team/Property" : "Uploader",
        value: uploadedByChip.label,
        onRemove: () => {
          const next: Record<string, FilterValue> = { ...appliedFilters };
          delete next.ctby;
          delete (next as any).must_not;
          delete (next as any)[`ctby${dvk}`];
          // Only clear team when it was set by the "My team" preset, not when
          // it was set independently alongside an uploader filter.
          if (uploadedByChip?.label === "My team") {
            delete next.team;
            delete (next as Record<string, FilterValue>)[`team${dvk}`];
          }
          delete (next as any).page;
          trackEvent("browse", {
            category: "filters",
            entity: "AppliedChip",
            action: "remove:UploadedBy",
            location: "content",
            ...getTrackUserContext(),
          });
          onRemove(next);
        },
      });
    }
    return list;
  }, [appliedFilters, filterByName, onRemove, uploadedByChip]);

  if (chips.length === 0) return null;

  return (
    <div className="afc-bar">
      <style>{appliedChipsStyles}</style>
      <span className="afc-filter-icon" aria-hidden="true">
        <ListFilter size={16} strokeWidth={1.8} />
      </span>
      <div className="afc-track">
        {chips.map((chip) => (
          <span
            key={chip.key}
            className="afc-chip"
            role="button"
            tabIndex={0}
            onClick={onOpenSheet}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenSheet();
              }
            }}
            title="Edit filter"
          >
            <span className="afc-chip-label">{chip.label}</span>
            <span className="afc-chip-sep" aria-hidden="true">
              :
            </span>
            <span className="afc-chip-value">
              {truncateText(chip.value, 28)}
            </span>
            <span
              role="button"
              tabIndex={0}
              className="afc-chip-remove"
              onClick={(e) => {
                e.stopPropagation();
                chip.onRemove();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  e.preventDefault();
                  chip.onRemove();
                }
              }}
              aria-label={`Remove ${chip.label} filter`}
            >
              <X size={22} strokeWidth={1.5} />
            </span>
          </span>
        ))}
      </div>
      {chips.length > 1 && (
        <button type="button" className="afc-clear" onClick={onClearAll}>
          Clear filters
        </button>
      )}
    </div>
  );
};

const appliedChipsStyles = `
.afc-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  padding: 4px 16px;
  background: #ffffff;
  border-bottom: 1px solid #dee2e6;
}
.afc-filter-icon {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: #6b7280;
}
.afc-track {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  padding: 2px 0;
}
.afc-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 8px 0 12px;
  background: #e9ecef;
  border: none;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  color: #212529;
  cursor: pointer;
  flex: 0 1 auto;
  min-width: 0;
  transition: background 140ms ease;
  user-select: none;
  text-transform: none;
}
.afc-chip:hover {
  background: #dee2e6;
}
.afc-chip:focus-visible {
  outline: 0;
  border-color: #6b7280;
  box-shadow: 0 0 0 2px rgba(107, 114, 128, 0.2);
}
.afc-chip-label {
  color: #212529;
  font-weight: 400;
  font-size: 12px;
}
.afc-chip-sep {
  color: #212529;
  font-size: 12px;
  line-height: 1;
}
.afc-chip-value {
  color: #212529;
  font-weight: 400;
  font-size: 12px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.afc-chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-left: 2px;
  border-radius: 999px;
  color: #212529;
  background: transparent;
  transition: background 120ms ease, color 120ms ease;
  cursor: pointer;
  flex-shrink: 0;
}
.afc-chip-remove:hover {
  background: rgba(0, 0, 0, 0.1);
  color: #212529;
}
.afc-clear {
  flex-shrink: 0;
  border: 0;
  background: transparent;
  color: #6b7280;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  text-transform: none;
  letter-spacing: 0;
  font-family: inherit;
  transition: color 120ms ease, background 120ms ease;
}
.afc-clear:hover {
  color: #111827;
  background: rgba(0, 0, 0, 0.06);
}
`;

export default AppliedFilterChips;
